import {
    type ReactNode,
    useEffect,
    useMemo,
    useState,
} from 'react'
import {
    APIQueueTaskKind,
    APIQueueTaskStatus,
    type APIOrganization,
    type APIQueueTaskResult,
    type APIQueueTask,
    type PaginatedRequestQueryParams,
    type DocumentQueueTasks,
    QueueTaskMetadataMap,
} from '@visorpro/client'
import {
    MaterialReactTable,
    type MRT_ColumnDef,
    type MRT_Updater,
    type MRT_ColumnFiltersState,
    type MRT_PaginationState,
    type MRT_Row,
    type MRT_RowData,
    type MRT_TableInstance,
    type MRT_TableOptions,
    type MRT_TableState,
} from 'material-react-table'
import _ from 'lodash'
import {
    Block,
    HistoryToggleOff,
    Remove,
    Warning,
    CheckCircle,
} from '@mui/icons-material'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    type IconButtonProps,
    InputLabel,
    Paper,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import { type DateTime } from 'luxon'
import { useAdminMaterialReactTable } from './table/mui-table'
import { useMultiValueColumnDef } from './table/column-definition'
import { formatDate } from '../util/format'
import { type APIGetQueueTaskRequestFilteringParams } from '@visorpro/client/dist/PipelineClient/services'
import { observer } from 'mobx-react-lite'
import { type QueueTasksStore } from '../stores/queue-tasks-store'
import { SplitButtons } from './split-button'
import { TaskTimeline } from './task-timeline'
import {
    createQueueTaskOptions,
    type CreateQueueTaskOption,
} from '../util/document-queue-tasks'
import type { DictionaryArray } from '../types'

export interface QueueTasksTableProps {
    tasks: APIQueueTask[]
    isLoading?: boolean
    isCreating?: boolean
    rowCount?: number
    pagination?: PaginatedRequestQueryParams
    filters?: Omit<APIGetQueueTaskRequestFilteringParams, 'offset' | 'limit'>
    sortings?: MRT_TableState<APIQueueTask>['sorting']
    expanded?: MRT_TableState<APIQueueTask>['expanded']
    setPagination?: (pagination: PaginatedRequestQueryParams) => void
    setFilters?: (
        filters: Omit<
            APIGetQueueTaskRequestFilteringParams,
            'offset' | 'limit'
        >,
    ) => void
    setSortings?: (state: MRT_TableState<APIQueueTask>['sorting']) => void
    onExpandedChange?: MRT_TableOptions<APIQueueTask>['onExpandedChange']
    renderDetailPanel?: (id: string) => ReactNode
    /**
     * Creates a task of the given kind for the page's document, or a chain
     * of tasks when given several kinds.
     */
    createDocumentQueueTask?: (
        kind: DocumentQueueTasks | DocumentQueueTasks[],
    ) => void
    createQueueTaskMappings?: {
        [APIQueueTaskKind.EXPORT_DIS_WORK_ORDERS]?: (metadata: QueueTaskMetadataMap[APIQueueTaskKind.EXPORT_DIS_WORK_ORDERS]) => void
        [APIQueueTaskKind.EXPORT_HBS_WORK_ORDERS]?: (metadata: QueueTaskMetadataMap[APIQueueTaskKind.EXPORT_HBS_WORK_ORDERS]) => void
    }
    isPrimaryBackgroundColor?: boolean
    /** Names the organizations that the tasks' metadata refers to. */
    organizations?: DictionaryArray<APIOrganization>
}

// The pipeline API returns at most 1000 tasks per page.
const ROWS_PER_PAGE_OPTIONS = [100, 500, 1000]

export const QueueTasksTable = ({
    tasks: data,
    organizations,
    rowCount,
    pagination,
    setPagination,
    filters,
    setFilters,
    sortings,
    setSortings,
    expanded,
    onExpandedChange,
    renderDetailPanel,
    isLoading,
    isCreating,
    createDocumentQueueTask,
    createQueueTaskMappings,
    isPrimaryBackgroundColor = false,
}: QueueTasksTableProps) => {
    const [isCreatingDocumentTask, setIsCreatingDocumentTask] = useState(false)
    const [documentTaskOption, setDocumentTaskOption] = useState(createQueueTaskOptions[0])
    const [isCreatingExportDisWorkOrdersTask, setIsCreatingExportDisWorkOrdersTask] = useState(false)
    const [isCreatingExportHbsWorkOrdersTask, setIsCreatingExportHbsWorkOrdersTask] = useState(false)

    const paginationState = useMemo(() => {
        if (pagination === undefined) return undefined

        return {
            pageIndex:
                pagination.offset && pagination.limit
                    ? pagination.offset / pagination.limit
                    : 0,
            pageSize: pagination.limit || 1000,
        }
    }, [pagination])

    const onPaginationChange = (
        pagination: MRT_Updater<MRT_PaginationState>,
    ) => {
        if (typeof pagination === 'function') {
            const newPagination = pagination(table.getState().pagination)
            setPagination?.({
                offset: newPagination.pageIndex * newPagination.pageSize,
                limit: newPagination.pageSize,
            })
        } else {
            setPagination?.({
                offset: pagination.pageIndex * pagination.pageSize,
                limit: pagination.pageSize,
            })
        }
    }

    const columnFiltersState = useMemo(() => {
        if (filters === undefined) return undefined

        const columnFilters: MRT_ColumnFiltersState = []
        Object.entries(filters).forEach(([key, value]) => {
            columnFilters.push({ id: key, value })
        })

        return columnFilters
    }, [filters])

    const onColumnFiltersChange = (
        filters: MRT_Updater<MRT_ColumnFiltersState>,
    ) => {
        const oldFilters = table.getState().columnFilters
        let newFilters: MRT_ColumnFiltersState

        if (typeof filters === 'function') {
            newFilters = filters(oldFilters)
        } else {
            newFilters = filters
        }

        if (_.isEqual(newFilters, oldFilters)) return

        const filtersBody: APIGetQueueTaskRequestFilteringParams = {}
        newFilters.forEach((filter) => {
            filtersBody[filter.id] = filter.value as
                | string
                | number
                | string[]
                | number[]
                | undefined
        })

        setFilters?.(filtersBody)
        table.firstPage()
    }

    const onClickDocumentTask = (option: CreateQueueTaskOption) => {
        setDocumentTaskOption(option)
        setIsCreatingDocumentTask(true)
    }
    const onExportDisWorkOrders = createQueueTaskMappings?.[APIQueueTaskKind.EXPORT_DIS_WORK_ORDERS]
    const onExportHbsWorkOrders = createQueueTaskMappings?.[APIQueueTaskKind.EXPORT_HBS_WORK_ORDERS]

    const getStatusOptions = (statusOptions: string[]) => {
        return statusOptions.map((status) => ({
            label: _.startCase(status),
            value: status,
        }))
    }
    const statusOptions = Object.values(APIQueueTaskStatus)
    const _statusColumnDefs = useMultiValueColumnDef<
        APIQueueTask,
        APIQueueTaskStatus
    >({
        fieldName: 'Status',
        optionsData: statusOptions,
        getOptions: getStatusOptions,
    })

    const getKindOptions = (kindOptions: string[]) => {
        return kindOptions.map((kind) => ({
            label: _.startCase(kind),
            value: kind,
        }))
    }

    const kindOptions = Object.values(APIQueueTaskKind)
    const kindColumnDefs = useMultiValueColumnDef<
        APIQueueTask,
        APIQueueTaskKind
    >({
        fieldName: 'Kind',
        optionsData: kindOptions,
        getOptions: getKindOptions,
    })

    const columns = useMemo<MRT_ColumnDef<APIQueueTask>[]>(
        () => [
            {
                accessorKey: 'status',
                header: 'Status',
                size: 120,
                Cell: ({ cell }) => {
                    const value = cell.getValue<string>()
                    const icon = getQueueTaskStatusIcon(
                        value as APIQueueTaskStatus,
                    )
                    return <Tooltip title={value}>{icon}</Tooltip>
                },
                // enableColumnFilter: setFilters !== undefined,
                // filterFn: 'equals',
                // ...statusColumnDefs,
            },
            {
                accessorKey: 'kind',
                header: 'Kind',
                size: 180,
                Cell: ({ cell }) => {
                    const value = cell.getValue<string>()
                    return _.startCase(value)
                },
                enableColumnFilter: setFilters !== undefined,
                filterFn: 'equals',
                ...kindColumnDefs,
            },
            {
                accessorKey: 'id',
                header: 'Task ID',
                size: 360,
                enableClickToCopy: true,
            },
            {
                id: 'timeline',
                header: 'Timeline',
                size: 600,
                minSize: 600,
                Cell: ({ row }) => (
                    <TaskTimeline
                        task={row.original}
                        organizations={organizations}
                    />
                ),
            },
            {
                accessorKey: 'created_at',
                header: 'Created At',
                size: 150,
                Cell: ({ cell }) => formatDate(cell.getValue<DateTime>()),
            },
            {
                accessorKey: 'started_at',
                header: 'Started At',
                size: 150,
                Cell: ({ cell }) => {
                    const value = cell.getValue<DateTime | null>()
                    return value ? formatDate(value) : null
                },
            },
            {
                accessorKey: 'ended_at',
                header: 'Ended At',
                size: 150,
                Cell: ({ cell }) => {
                    const value = cell.getValue<DateTime | null>()
                    return value ? formatDate(value) : null
                },
            },
            {
                accessorKey: 'updated_at',
                header: 'Updated At',
                size: 150,
                Cell: ({ cell }) => formatDate(cell.getValue<DateTime>()),
            },
        ],
        [kindColumnDefs, organizations, setFilters],
    )

    const renderRowDetailPanel: MRT_TableOptions<APIQueueTask>['renderDetailPanel'] =
        ({ row }) => {
            return <Box sx={{ m: -2 }}>
                {renderDetailPanel?.(row.original.id)}
            </Box>
        }

    const table = useAdminMaterialReactTable(
        {
            columns,
            data,
            rowCount,
            initialState: {
                columnPinning: {
                    left: [
                        'status',
                        'kind',
                        'id',
                    ],
                },
                // The timeline tells when a task was enqueued, started
                // and ended.
                columnVisibility: {
                    created_at: false,
                    started_at: false,
                    ended_at: false,
                },
            },
            defaultColumn: {
                minSize: 80,
                enableSorting: true,
            },
            enableColumnResizing: true,
            enableSorting: setSortings !== undefined,
            enableRowSelection: false,
            getRowId: (task) => task.id,
            muiPaginationProps: {
                rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
            },
            ...(setPagination && {
                manualPagination: true,
                onPaginationChange,
            }),
            ...(setFilters && { manualFiltering: true, onColumnFiltersChange }),
            ...(setSortings && {
                manualSorting: true,
                onSortChange: setSortings,
            }),
            ...(renderDetailPanel && {
                enableExpanding: true,
                enableExpandAll: false,
                renderDetailPanel: renderRowDetailPanel,
                onExpandedChange,
                muiExpandButtonProps: getExpandButtonProps,
            }),
            state: {
                isLoading,
                showProgressBars: isLoading || isCreating,
                ...(pagination &&
                    paginationState && { pagination: paginationState }),
                ...(filters &&
                    columnFiltersState && {
                    columnFilters: columnFiltersState,
                }),
                ...(sortings && { sorting: sortings }),
                ...(expanded && { expanded }),
            },
            renderTopToolbarCustomActions: () => {

                return (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {(createDocumentQueueTask || createQueueTaskMappings) && (
                            <SplitButtons popperWidth={225}>
                                {createDocumentQueueTask &&
                                    createQueueTaskOptions.map((option) => (
                                        <Button
                                            key={option.label}
                                            onClick={() => onClickDocumentTask(option)}
                                            disabled={isLoading}>
                                            {option.label}
                                        </Button>
                                    ))}
                                {onExportDisWorkOrders && (
                                    <Button
                                        onClick={() => setIsCreatingExportDisWorkOrdersTask(true)}
                                        disabled={isLoading}>
                                        Export DIS Work Orders
                                    </Button>
                                )}
                                {onExportHbsWorkOrders && (
                                    <Button
                                        onClick={() => setIsCreatingExportHbsWorkOrdersTask(true)}
                                        disabled={isLoading}>
                                        Export HBS Work Orders
                                    </Button>
                                )}
                            </SplitButtons>
                        )}
                    </Box>
                )
            },
            isPrimaryBackgroundColor,
        },
        'queue-tasks-table-v2',
    )

    return <>
        <MaterialReactTable table={table} />
        <CreateDocumentQueueTaskDialog
            open={isCreatingDocumentTask}
            setOpen={setIsCreatingDocumentTask}
            option={documentTaskOption}
            createQueueTask={createDocumentQueueTask}
        />
        <CreateExportDisWorkOrdersDialog
            open={isCreatingExportDisWorkOrdersTask}
            setOpen={setIsCreatingExportDisWorkOrdersTask}
            createQueueTask={onExportDisWorkOrders}
        />
        <CreateExportHbsWorkOrdersDialog
            open={isCreatingExportHbsWorkOrdersTask}
            setOpen={setIsCreatingExportHbsWorkOrdersTask}
            createQueueTask={onExportHbsWorkOrders}
        />
    </>
}

export interface QueueTaskDetailPanelProps {
    store: QueueTasksStore
    taskId: string
}

/**
 * The results of a listed task's descendants, which the list leaves out.
 * The task is fetched on its own while the panel is open, and again
 * whenever the list reports newer activity on it.
 */
export const QueueTaskDetailPanel = observer(
    ({ store, taskId }: QueueTaskDetailPanelProps) => {
        const updatedAt = store.tasks[taskId]?.updated_at.toMillis()

        useEffect(() => {
            void store.get(taskId)
        }, [store, taskId, updatedAt])

        useEffect(() => () => store.forget(taskId), [store, taskId])

        const task = store.details[taskId]
        return (
            <QueueTaskResultsTable
                tasks={task?.descendants ?? []}
                isLoading={!task}
            />
        )
    },
)

export interface QueueTaskResultsTableProps {
    tasks: APIQueueTaskResult[]
    isLoading?: boolean
}

export const QueueTaskResultsTable = ({
    tasks,
    isLoading,
}: QueueTaskResultsTableProps) => {
    const columns = useMemo<MRT_ColumnDef<APIQueueTaskResult>[]>(
        () => [
            {
                accessorKey: 'task_id',
                header: 'Task ID',
                size: 360,
                enableClickToCopy: true,
            },
            {
                accessorKey: 'task_name',
                header: 'Task Name',
                size: 250,
                enableClickToCopy: true,
            },
            {
                accessorKey: 'status',
                header: 'Status',
                size: 80,
                Cell: ({ cell }) => {
                    const value = cell.getValue<string>()
                    const icon = getQueueTaskStatusIcon(
                        value as APIQueueTaskStatus,
                    )
                    return <Tooltip title={value}>{icon}</Tooltip>
                },
            },
            {
                accessorKey: 'meta',
                header: 'Meta',
                size: 250,
                enableClickToCopy: true,
            },
            {
                accessorKey: 'result',
                header: 'Result',
                size: 250,
                enableClickToCopy: true,
                Cell: ({ cell }) =>
                    formatResultPreview(cell.getValue<string | null>()),
            },
            {
                accessorKey: 'traceback',
                header: 'Error',
                size: 250,
                enableClickToCopy: true,
                Cell: ({ cell }) =>
                    getTracebackError(cell.getValue<string | null>()),
            },
            {
                accessorKey: 'started_at',
                header: 'Started At',
                size: 150,
                Cell: ({ cell }) => {
                    const value = cell.getValue<DateTime | null>()
                    return value ? formatDate(value) : null
                },
            },
            {
                accessorKey: 'finished_at',
                header: 'Finished At',
                size: 150,
                Cell: ({ cell }) => {
                    const value = cell.getValue<DateTime | null>()
                    return value ? formatDate(value) : null
                },
            },
            {
                accessorKey: 'retries',
                header: 'Retries',
                size: 80,
            },
            {
                accessorKey: 'worker',
                header: 'Worker',
                enableClickToCopy: true,
            },
            {
                accessorKey: 'parent_id',
                header: 'Parent ID',
                size: 360,
                enableClickToCopy: true,
            },
            {
                accessorKey: 'root_id',
                header: 'Root ID',
                size: 360,
                enableClickToCopy: true,
            },
            {
                accessorKey: 'group_id',
                header: 'Group ID',
                size: 360,
                enableClickToCopy: true,
            },
        ],
        [],
    )

    const table = useAdminMaterialReactTable(
        {
            columns,
            data: tasks,
            enableExpanding: true,
            enableExpandAll: false,
            enableEditing: false,
            enableFilters: false,
            enableRowSelection: false,
            enableStickyHeader: true,
            enableStickyFooter: false,
            enableColumnOrdering: false,
            enableColumnPinning: false,
            enablePagination: false,
            enableColumnActions: false,
            enableColumnDragging: false,
            enableColumnResizing: true,
            enableHiding: false,
            enableTopToolbar: false,
            enableBottomToolbar: false,
            getRowId: (result) => result.task_id,
            muiExpandButtonProps: (args) => ({
                ...getExpandButtonProps(args),
                sx: { ml: 3 },
            }),
            renderDetailPanel: ({ row }) => (
                <Stack
                    direction="column"
                    useFlexGap
                    spacing={1.6}
                    sx={{ width: '100%', flexWrap: 'wrap' }}>
                    <Box>
                        <Typography variant="overline">Meta</Typography>
                        <ObjectPreviewer data={row.original.meta} />
                    </Box>
                    <Box>
                        <Typography variant="overline">
                            Result
                        </Typography>
                        <ObjectPreviewer data={row.original.result} />
                    </Box>
                    <Box>
                        <Typography variant="overline">Traceback</Typography>
                        <TextPreviewer text={row.original.traceback} />
                    </Box>
                </Stack>
            ),
            state: {
                showProgressBars: false,
                isLoading,
            },
            muiTableContainerProps: {
                sx: {
                    maxHeight: '50rem',
                }
            },
        },
        'queue-task-results-table',
    )

    return <MaterialReactTable table={table} />
}

interface ExpandButtonPropsArgs<TData extends MRT_RowData> {
    row: MRT_Row<TData>
    table: MRT_TableInstance<TData>
}

// Points the expand chevron sideways while the row is collapsed and
// downwards once it is expanded.
const getExpandButtonProps = <TData extends MRT_RowData>({
    row,
    table,
}: ExpandButtonPropsArgs<TData>): IconButtonProps => {
    const { ExpandMoreIcon } = table.options.icons

    return {
        children: (
            <ExpandMoreIcon
                style={{
                    transform: `rotate(${row.getIsExpanded() ? 0 : -90}deg)`,
                    transition: 'transform 150ms',
                }}
            />
        ),
    }
}

// The start of a result, which may be too long for a cell.
const formatResultPreview = (result: string | null) =>
    result && _.truncate(result, { length: 100, omission: '…' })

// The last line of a traceback, which names the exception that was raised.
const getTracebackError = (traceback: string | null) =>
    traceback && _.last(traceback.trimEnd().split('\n'))

const ObjectPreviewer = ({ data }: { data: string | null }) => {
    let formattedData = data

    try {
        if (data) {
            formattedData = JSON.stringify(JSON.parse(data), null, 2)
        }
    } catch {
        formattedData = data
    }

    return <TextPreviewer text={formattedData} />
}

const TextPreviewer = ({ text }: { text: string | null }) => (
    <Paper
        elevation={0}
        sx={{
            padding: 2,
            overflow: 'auto',
            whiteSpace: 'normal',
            fontFamily: 'monospace',
            width: '100%',
            maxHeight: '30rem',
        }}>
        <pre>{text}</pre>
    </Paper>
)

export const getQueueTaskStatusIcon = (status?: string) => {
    if (status === APIQueueTaskStatus.PENDING) {
        return (
            <HistoryToggleOff
                sx={{ color: (theme) => theme.palette.warning.light }}
                fontSize="small"
            />
        )
    } else if (status === APIQueueTaskStatus.STARTED) {
        return (
            <CircularProgress
                variant="indeterminate"
                size={18}
                thickness={6}
                color="primary"
            />
        )
    } else if (status === APIQueueTaskStatus.SUCCESS) {
        return (
            <CheckCircle
                sx={{ color: (theme) => theme.palette.success.light }}
                fontSize="small"
            />
        )
    } else if (status === APIQueueTaskStatus.FAILURE) {
        return (
            <Warning
                sx={{ color: (theme) => theme.palette.error.light }}
                fontSize="small"
            />
        )
    } else if (status === APIQueueTaskStatus.REVOKED) {
        return <Warning fontSize="small" sx={{ color: 'grey' }} />
    } else if (status === APIQueueTaskStatus.IGNORED) {
        return <Block fontSize="small" sx={{ color: 'grey' }} />
    }
    return <Remove fontSize="small" sx={{ color: 'grey' }} />
}


interface CreateDocumentQueueTaskDialogProps {
    open: boolean
    setOpen: (open: boolean) => void
    option: CreateQueueTaskOption
    createQueueTask?: (kind: DocumentQueueTasks | DocumentQueueTasks[]) => void
}

const CreateDocumentQueueTaskDialog = ({
    open,
    setOpen,
    option,
    createQueueTask
}: CreateDocumentQueueTaskDialogProps) => {
    const onCreate = () => {
        createQueueTask?.(option.value)
        onClose()
    }
    const onClose = () => setOpen(false)

    return (
        <Dialog open={open} onClose={onClose} fullWidth>
            <DialogTitle>Create {_.lowerFirst(option.label)} task</DialogTitle>
            <DialogContent>
                <DialogContentText>{option.description}</DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button color="primary" onClick={onCreate}>
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
    )
}

interface CreateExportHbsWorkOrdersDialogProps {
    open: boolean
    setOpen: (open: boolean) => void
    createQueueTask?: (metadata: QueueTaskMetadataMap[APIQueueTaskKind.EXPORT_HBS_WORK_ORDERS]) => void
}

const CreateExportHbsWorkOrdersDialog = ({
    open,
    setOpen,
    createQueueTask
}: CreateExportHbsWorkOrdersDialogProps) => {
    const [hbsDomain, setHbsDomain] = useState<string>('')
    const [hbsUsername, setHbsUsername] = useState<string>('')
    const [hbsPassword, setHbsPassword] = useState<string>('')

    const onCreate = () => {
        createQueueTask?.({
            organization_id: '',
            hbs_domain: hbsDomain,
            hbs_username: hbsUsername,
            hbs_password: hbsPassword,
        })
        onClose()
    }
    const onClose = () => setOpen(false)

    useEffect(() => {
        if (open) {
            setHbsDomain('')
            setHbsUsername('')
            setHbsPassword('')
        }
    }, [open])

    return (
        <Dialog open={open} onClose={onClose} fullWidth>
            <DialogTitle>Create export HBS work orders task</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    <Stack spacing={1} sx={{ py: 1 }}>
                        <TextField
                            required
                            id="create-export-hbs-work-orders-task-hbs-domain"
                            label="HBS Domain"
                            value={hbsDomain}
                            onChange={(event) => setHbsDomain(event.target.value)}
                        />
                        <TextField
                            required
                            id="create-export-hbs-work-orders-task-hbs-username"
                            label="HBS Username"
                            value={hbsUsername}
                            onChange={(event) => setHbsUsername(event.target.value)}
                        />
                        <TextField
                            required
                            id="create-export-hbs-work-orders-task-hbs-password"
                            label="HBS Password"
                            type="password"
                            value={hbsPassword}
                            onChange={(event) => setHbsPassword(event.target.value)}
                        />
                    </Stack>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    color="primary"
                    onClick={onCreate}
                    disabled={
                        hbsDomain === '' || hbsUsername === '' || hbsPassword === ''
                    }>
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
    )
}

interface CreateExportDisWorkOrdersDialogProps {
    open: boolean
    setOpen: (open: boolean) => void
    createQueueTask?: (metadata: QueueTaskMetadataMap[APIQueueTaskKind.EXPORT_DIS_WORK_ORDERS]) => void
}

const CreateExportDisWorkOrdersDialog = ({
    open,
    setOpen,
    createQueueTask
}: CreateExportDisWorkOrdersDialogProps) => {
    const [disDomain, setDisDomain] = useState<string>('')
    const [disUsername, setDisUsername] = useState<string>('')
    const [disPassword, setDisPassword] = useState<string>('')

    const onCreate = () => {
        createQueueTask?.({
            organization_id: '',
            dis_domain: disDomain,
            dis_username: disUsername,
            dis_password: disPassword,
        })
        onClose()
    }
    const onClose = () => setOpen(false)

    useEffect(() => {
        if (open) {
            setDisDomain('')
            setDisUsername('')
            setDisPassword('')
        }
    }, [open])

    return (
        <Dialog open={open} onClose={onClose} fullWidth>
            <DialogTitle>Create export DIS work orders task</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    <Stack spacing={1} sx={{ py: 1 }}>
                        <TextField
                            required
                            id="create-export-dis-work-orders-task-dis-domain"
                            label="DIS Domain"
                            value={disDomain}
                            onChange={(event) => setDisDomain(event.target.value)}
                        />
                        <TextField
                            required
                            id="create-export-dis-work-orders-task-dis-username"
                            label="DIS Username"
                            value={disUsername}
                            onChange={(event) => setDisUsername(event.target.value)}
                        />
                        <TextField
                            required
                            id="create-export-dis-work-orders-task-dis-password"
                            label="DIS Password"
                            type="password"
                            value={disPassword}
                            onChange={(event) => setDisPassword(event.target.value)}
                        />
                    </Stack>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    color="primary"
                    onClick={onCreate}
                    disabled={
                        disDomain === '' || disUsername === '' || disPassword === ''
                    }>
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
    )
}